import { useEffect, useState } from 'react'

import {
  type CompanyData,
  getCompanies as getFactorialIdCompanies,
} from 'modules/factorial_id/lib/api_client'

type FetchState =
  | { status: 'loading' }
  | { status: 'success'; companies: CompanyData[] }
  | { status: 'error'; error: Error }

interface UseFactorialIdCompaniesResult {
  data: CompanyData[] | undefined
  loading: boolean
  error: Error | undefined
}

/**
 * Fetches the list of companies from Factorial ID for the current user.
 *
 * Mirrors the { data, loading, error } shape of useQuery so callers can treat
 * both data sources the same way.
 *
 * Pass `skip: true` to suppress the fetch (e.g. when the session was not
 * issued by Factorial ID). Callers that pass skip are expected not to use the
 * result — the returned shape is unspecified when skipped.
 */
export const useFactorialIdCompanies = ({
  skip,
}: {
  skip: boolean
}): UseFactorialIdCompaniesResult => {
  const [state, setState] = useState<FetchState>({ status: 'loading' })

  useEffect(() => {
    if (skip) return

    setState({ status: 'loading' })

    getFactorialIdCompanies()
      .then((companies) => setState({ status: 'success', companies }))
      .catch((error: unknown) => {
        const normalizedError = error instanceof Error ? error : new Error(String(error))
        setState({ status: 'error', error: normalizedError })
      })
  }, [skip])

  if (state.status === 'success') {
    return { data: state.companies, loading: false, error: undefined }
  }

  if (state.status === 'error') {
    return { data: undefined, loading: false, error: state.error }
  }

  return { data: undefined, loading: true, error: undefined }
}
